// src/frontend/js/modern/ui/layout-handler.js

import { queueManager } from '../core/queue.js';
import { eventBus, EVENTS } from '../core/event-bus.js';
import { block, unblock } from '../shared/ui-helpers.js';

export class LayoutHandler {
	constructor() {
		this._boundEvents = [];
	}

	init() {
		this._bindLayoutToggle();
		this._bindTabs();
		this._bindTabbedPageLinks();
	}

	_bindLayoutToggle() {
		const handler = (e) => {
			const btn = e.target.closest('.wlfmc-toggle-layout');
			if (!btn) return;
			e.preventDefault();

			// Instant visual toggle — never block or delay the user.
			btn.classList.toggle('list');
			btn.classList.toggle('grid');
			const table = btn.closest('.wlfmc-wishlist-form')?.querySelector('.wlfmc-list');
			if (table) {
				table.classList.toggle('view-mode-list');
				table.classList.toggle('view-mode-grid');
			}

			const newLayout = btn.classList.contains('list') ? 'grid' : 'list';
			const nonce = btn.dataset.nonce;

			// addWithDeduplication guarantees only the LAST request for
			// scope 'core::change_layout::global' actually reaches the server.
			// All previous in-flight/queued requests are aborted automatically.
			queueManager.addWithDeduplication('change_layout', {
				action: 'wlfmc_change_layout',
				new_layout: newLayout,
				nonce: nonce,
				context: 'frontend'
			}).catch((error) => {
				// AbortError means a NEWER click superseded this request.
				// The newer click already owns the visual state — skip rollback.
				if (error && error.name === 'AbortError') return;
				// Real failure (network/server): revert the optimistic toggle.
				btn.classList.toggle('list');
				btn.classList.toggle('grid');
				if (table) {
					table.classList.toggle('view-mode-list');
					table.classList.toggle('view-mode-grid');
				}
			});
		};
		document.addEventListener('click', handler);
		this._boundEvents.push({ target: document, event: 'click', handler });
	}

	_bindTabs() {
		const handler = (e) => {
			const link = e.target.closest('.wlfmc-tabs a:not(.external-link)');
			if (!link) return;
			e.preventDefault();

			const content = link.dataset.content;
			const wrapper = link.closest('.wlfmc-tabs-wrapper');
			if (!wrapper) return;

			wrapper.classList.remove('active-tab-cart', 'active-tab-save-for-later', 'active-tab-wishlist', 'active-tab-waitlist', 'active-tab-lists');
			wrapper.classList.add('active-tab-' + content);

			wrapper.querySelectorAll('.wlfmc-tab-content').forEach(el => el.style.display = 'none');
			wrapper.querySelectorAll('.wlfmc-tabs a').forEach(el => el.classList.remove('nav-tab-active'));
			link.classList.add('nav-tab-active');

			const targetContent = wrapper.querySelector(`.wlfmc_content_${content}`);
			if (targetContent) targetContent.style.display = 'block';

			const url = new URL(window.location.href);
			url.searchParams.set('tab', content);
			window.history.replaceState({}, '', url.toString());

			if (content === 'save-for-later') {
				eventBus.emit(EVENTS.CONTENT_UPDATED);
			}
		};
		document.addEventListener('click', handler);
		this._boundEvents.push({ target: document, event: 'click', handler });
	}

	_bindTabbedPageLinks() {
		const handler = (e) => {
			const link = e.target.closest('.wlfmc-lists-header a:not(.external-link), a.wlfmc-open-list-link');
			if (!link) return;

			const href = link.getAttribute('href');
			if (!href || href === '#' || href === '#!' || href.includes('javascript:')) return;

			if (!document.body.classList.contains('elementor-editor-active')) {
				return;
			}

			const wrapper = link.closest('.wlfmc-wishlist-page');
			if (!wrapper) return;

			e.preventDefault();
			e.stopPropagation();

			const tabContent = wrapper.querySelector('.wlfmc-tab-content');
			if (tabContent) {
				block(tabContent);
			}

			this._loadTabContentWithBlock(href, wrapper, tabContent);
		};
		document.addEventListener('click', handler);
		this._boundEvents.push({ target: document, event: 'click', handler });
	}

	_loadTabContentWithBlock(url, wrapper, tabContent) {
		fetch(url, { method: 'GET', credentials: 'same-origin' })
			.then(response => response.text())
			.then(html => {
				const temp = document.createElement('div');
				temp.innerHTML = html;

				const newHeader = temp.querySelector('.wlfmc-lists-header');
				const newContent = temp.querySelector('.wlfmc-tab-content');

				if (newHeader) {
					const oldHeader = wrapper.querySelector('.wlfmc-lists-header');
					if (oldHeader) oldHeader.replaceWith(newHeader);
				}

				if (newContent) {
					const oldContent = wrapper.querySelector('.wlfmc-tab-content');
					if (oldContent) oldContent.replaceWith(newContent);
				}

				window.history.pushState({ path: url }, '', url);
				eventBus.emit(EVENTS.CONTENT_UPDATED);
			})
			.catch(error => {
				console.warn('[LayoutHandler] Load tab content failed:', error);
			})
			.finally(() => {
				const currentTabContent = wrapper.querySelector('.wlfmc-tab-content');
				if (currentTabContent) {
					unblock(currentTabContent);
				} else if (tabContent) {
					unblock(tabContent);
				}
			});
	}

	destroy() {
		this._boundEvents.forEach(({ target, event, handler }) => {
			target.removeEventListener(event, handler);
		});
		this._boundEvents = [];
	}
}
